import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { Destination, DestinationMetadataApiClient, DestinationProviderApiClient } from './DestinationsApiClient';
import { BadRequestError, UnauthenticatedError } from '../utils/auth';
import { AuthServer } from './AuthServer';
import { isLocalHost } from '../utils/isLocalHost';
import { AbortedError } from '../types/errors';

type TwitchOutputAuthInfo = {
  accessToken: string;
  tokenType: string;
  providerId: string;
};

type TwitchCategoryResponse = {
  id: string;
  name: string;
  box_art_url: string;
};

export class TwitchApiClient implements DestinationProviderApiClient<'twitch'> {
  static CLIENT_ID = '5sj4swxpmnoyd0szka0ruk8df67th3';
  static RTMP_SERVER_URL = 'rtmp://live.twitch.tv/app';
  private authContextProvider: AuthContextProvider;
  private serviceEnvironment: ServiceEnvironment;
  /**
   * This is an access token received to perform API calls on behalf of the user for
   * scopes that we don't have on the channel.accessToken.
   */
  private destinationMetadata: Destination;

  private static async getAuthHeaders({ accessToken }: { accessToken?: string } = {}) {
    const headers: Record<string, string> = {
      'Client-Id': TwitchApiClient.CLIENT_ID,
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
  }

  public async getAuthHeaders() {
    const { accessToken } = this.destinationMetadata;
    return TwitchApiClient.getAuthHeaders({ accessToken });
  }

  constructor({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata,
  }: {
    authContextProvider: AuthContextProvider;
    serviceEnvironment: ServiceEnvironment;
    destinationMetadata: Destination;
  }) {
    this.authContextProvider = authContextProvider;
    this.serviceEnvironment = serviceEnvironment;
    this.destinationMetadata = destinationMetadata;
  }

  public async endBroadcast() {
    // Method is not relevant for Twitch
    // Exists because this class implements a unified interface
  }

  private static async makeRequest<T>(
    endpoint: string,
    options: RequestInit & { params?: { [k: string]: unknown } } = {}
  ): Promise<T> {
    const headers = await this.getAuthHeaders();
    const searchParams = new URLSearchParams();

    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value === undefined) {
          return;
        }

        searchParams.append(key, `${value}`);
      });
    }

    const response = await fetch(`https://api.twitch.tv/${endpoint}?${searchParams.toString()}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...options.headers,
      },
    });

    if (response.ok) {
      try {
        const json = await response.json();
        return json as T;
      } catch (err) {
        // Api returned 200 but no json was returned
        return undefined as never;
      }
    }

    switch (true) {
      case response.status === 401:
        throw new UnauthenticatedError('Unauthenticated');
      case response.status === 400:
        throw new BadRequestError(`Failed to fetch ${endpoint}`);
      default:
        throw new Error(`Failed to fetch ${endpoint}`);
    }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit & { params?: { [k: string]: unknown } } = {}
  ): Promise<T> {
    const headers = await this.getAuthHeaders();

    return TwitchApiClient.makeRequest(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...options.headers,
      },
    });
  }

  public static getManageBroadcastScopeAuthorizationUrl({
    redirectUri,
    serverUrl,
  }: {
    redirectUri: string;
    serverUrl: string;
  }) {
    const state = btoa(JSON.stringify({ serverUrl }));
    const url = new URL('https://id.twitch.tv/oauth2/authorize');
    url.searchParams.set('response_type', 'token');
    url.searchParams.set('force_verify', 'true');
    url.searchParams.set('client_id', TwitchApiClient.CLIENT_ID);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('scope', 'channel:read:stream_key channel:manage:broadcast channel_read');
    url.searchParams.set('state', state);
    return url.toString();
  }

  public async fetchStreamKey() {
    const response = await this.makeRequest<{
      data: [
        {
          stream_key: string;
        },
      ];
    }>('helix/streams/key', {
      params: {
        broadcaster_id: this.destinationMetadata.providerId,
      },
    }).then((r) => {
      const {
        data: [{ stream_key }],
      } = r;
      return stream_key;
    });

    return response;
  }

  public async getStreamingOutputInfo() {
    const streamKey = await this.fetchStreamKey();

    return {
      streamKey,
      serverUrl: TwitchApiClient.RTMP_SERVER_URL,
    };
  }

  public async updateChannelMetadata({ title, gameId }: { title?: string; gameId?: string }) {
    const response = await this.makeRequest('helix/channels', {
      method: 'PATCH',
      params: {
        broadcaster_id: this.destinationMetadata.providerId,
      },
      body: JSON.stringify({
        title,
        game_id: gameId,
      }),
    });

    return response;
  }

  public static async getUserInfo(params: { accessToken: string; tokenType: string }): Promise<{
    id: string;
    login: string;
    display_name: string;
    type: string;
    broadcaster_type: string;
    description: string;
  }> {
    const response = await fetch('https://api.twitch.tv/helix/users', {
      headers: {
        Authorization: `Bearer ${params.accessToken}`,
        'Client-Id': TwitchApiClient.CLIENT_ID,
      },
    });

    const jsonResponse = await response.json().catch((err) => {
      console.error('Failed to parse JSON response:', err);
      throw new Error('Failed to fetch user info');
    });

    return jsonResponse.data[0];
  }

  public async setupStreamRoom({ title, categoryId }: { title: string; categoryId: string }) {
    const { streamKey, serverUrl } = await this.getStreamingOutputInfo();

    await this.updateChannelMetadata({
      title,
      gameId: categoryId,
    }).catch(console.error);

    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      destinationMetadata: this.destinationMetadata,
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
    });

    return destinationMetadataApiClient.updateDestination({
      outputs: this.destinationMetadata.outputs
        .map((output) => {
          output.streamingSettings.streamKey = streamKey;
          output.streamingSettings.serverUrl = serverUrl;
          output.streamingSettings.type = 'rtmp_common';
          output.auxiliaryData = {
            ...output.auxiliaryData,
            isPrimaryOutput: output.auxiliaryData?.isPrimaryOutput ?? false,
            destinationId: this.destinationMetadata.id,
            streamTitle: title,
            streamCategory: categoryId,
          };
          return output;
        })
        .slice(0, 1),
    });
  }

  public async updateStreamRoom({ title, categoryId }: { title?: string; categoryId?: string }) {
    await this.updateChannelMetadata({
      title,
      gameId: categoryId,
    }).catch(console.error);
  }

  public static async passTwitchCallbackParams(params: TwitchOutputAuthInfo & { serverUrl: string }) {
    console.log('Passing twitch callback params to auth server', params);

    return fetch(`${params.serverUrl}/auth/twitch/callback`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  public static getServerUrlFromState(state: string) {
    try {
      const parsedState = JSON.parse(atob(state));
      return parsedState.serverUrl;
    } catch (err) {
      return null;
    }
  }

  public static async connectTwitch() {
    const redirectUri = new URL(
      '/auth/twitch/callback',
      isLocalHost() ? 'http://localhost:3000' : 'https://selive.streamelements.com'
    );

    const twitchAuthServer = new AuthServer({ provider: 'twitch' });
    await twitchAuthServer.listen();

    const authorizationUrl = await TwitchApiClient.getManageBroadcastScopeAuthorizationUrl({
      redirectUri: redirectUri.toString(),
      serverUrl: twitchAuthServer.url,
    });

    let removeListener = () => {
      void 0;
    };

    const twitchAuthPromise = new Promise<TwitchOutputAuthInfo>((resolve, reject) => {
      removeListener = twitchAuthServer.onAuthCallback(async (result) => {
        if (!result) {
          return reject(new AbortedError('Output connection aborted'));
        }

        const { accessToken, providerId } = result;

        if (!accessToken || !providerId) {
          return reject(new Error('Invalid access token or provider id'));
        }

        return resolve(result);
      });
    });

    window.host.openDefaultBrowser(authorizationUrl, () => {
      console.log('Opened browser');
    });

    const result = await twitchAuthPromise;

    removeListener?.();

    await twitchAuthServer.dispose();

    return result;
  }

  public async getCategories({ query }: { query: string }): Promise<Map<string, string>> {
    const response = await this.makeRequest<{ data: TwitchCategoryResponse[] }>('helix/search/categories', {
      params: {
        query,
      },
    });

    return new Map(response.data.map((category) => [category.id, category.name]));
  }

  public async getCategoryById(params: { categoryId: string }): Promise<{ id: string; displayName: string } | null> {
    void params;
    return null;
  }
}
